/* 양/불 검사 판정 생성기와 관련 상수.
 * 사출기 모니터링의 양/불 탭과 AI 검사 모달이 같은 값을 보여야 해서 공용 모듈로 뺐다.
 * 백엔드 연동 시 buildInspectionSample() 호출부만 API 응답으로 교체하면 된다. */

export const PF_TEXT = {
  back: '뒤로가기',
  compareTitle: '설정값 vs 실측값 비교',
  confidence: '신뢰도',
  cushion: '쿠션량',
  defect: '불량',
  defectBubble: '기포',
  defectBurr: '버(Burr)',
  defectDiscolor: '변색',
  defectScratch: '스크래치',
  defectShort: '미성형',
  defectType: '검출 결함',
  finalThreshold: '판정 기준',
  finalTotal: '종합 점수',
  good: '양품',
  heaterTemp: '히터온도',
  injectionInfo: '사출정보',
  injectionPosition: '사출위치',
  injectionSpeed: '사출속도',
  injectionTime: '사출시간',
  inspectionFinal: '최종 결과',
  inspectionProcess: '공정검사',
  inspectionThermal: '열화상검사',
  inspectionVision: '비전검사',
  item: '항목',
  lstmAnalysis: 'LSTM 불량 원인 분석',
  measuredLegend: '실측값',
  meteringPosition: '계량위치',
  noDefect: '이상 없음',
  noImage: '이미지 없음',
  none: '미판정',
  passFailDefect: '불',
  passFailGood: '양',
  passFailHeader: '양/불',
  passFailNone: '미',
  productImage: '제품 이미지',
  productName: '제품명',
  productNumber: '제품번호',
  sampleNotice: '예시 데이터',
  selectShotFirst: '샷 이력에서 샷을 선택하세요',
  settingLegend: '설정값',
  settingValue: '셋팅값',
  shotHistory: 'Shot History',
  shotHistoryEmpty: '샷 이력 데이터가 없습니다.',
  tempAvg: '평균',
  tempMax: '최고',
  tempMin: '최저',
  tempSpec: '기준 범위',
};

/* ===== 양/불 검사 카드 설정 =====
 * 가중치 합은 1.0. 실제 판정 기준이 확정되면 이 값만 바꾸면 된다.
 * 비전/열화상은 아직 백엔드 연동 전이라 buildInspectionSample() 의 예시 데이터를 쓴다.
 * 연동 시 이 함수 호출부만 API 응답으로 교체하면 UI는 그대로 동작한다. */
export const INSPECTION_WEIGHTS = { process: 0.4, vision: 0.4, thermal: 0.2 };
export const INSPECTION_PASS_THRESHOLD = 0.85;
export const THERMAL_SPEC = { min: 30, max: 70 };

export const DEFECT_KEYS = ['defectShort', 'defectBurr', 'defectBubble', 'defectScratch', 'defectDiscolor'];

/* 예시 불량 발생률 — 실연동 전까지 화면에 양품/불량이 섞여 보이도록 하는 값. */
export const VISION_NG_RATE = 0.06;
export const THERMAL_NG_RATE = 0.05;

/* 샷 번호로 결정적(deterministic)인 예시값을 만든다.
 * 매 렌더마다 값이 흔들리면 안 되므로 난수 대신 샷 번호를 해시한다. */
export const buildInspectionSample = (shotNum, processStatus) => {
  const n = Number(shotNum) || 0;

  /* 정수 해시 — 연속한 샷 번호가 비슷한 값으로 이어지지 않게 비트를 섞는다. */
  const rand = (salt) => {
    let x = (Math.imul(n + 1, 2654435761) ^ Math.imul(salt, 40503)) >>> 0;
    x ^= x >>> 13;
    x = Math.imul(x, 1274126177) >>> 0;
    x ^= x >>> 16;
    return (x >>> 0) / 4294967296;
  };

  /* 공정검사는 실제 샷의 양/불 판정을 그대로 따른다. */
  const processOk = !(processStatus === 'BAD' || processStatus === 'DEFECT');
  const processScore = processOk ? 0.88 + rand(1) * 0.11 : 0.40 + rand(1) * 0.28;

  const visionOk = rand(2) >= VISION_NG_RATE;
  const visionScore = visionOk ? 0.88 + rand(3) * 0.11 : 0.45 + rand(3) * 0.30;

  const thermalOk = rand(4) >= THERMAL_NG_RATE;
  const thermalScore = thermalOk ? 0.86 + rand(5) * 0.13 : 0.50 + rand(5) * 0.28;

  /* 열화상 불량이면 최고온도가 기준을 넘도록 맞춘다 (화면 표시와 판정이 어긋나지 않게). */
  const tempMax = thermalOk
    ? THERMAL_SPEC.max - 14 + rand(6) * 12
    : THERMAL_SPEC.max + 1 + rand(6) * 8;
  const tempMin = THERMAL_SPEC.min + rand(7) * 6;
  const tempAvg = (tempMax + tempMin) / 2;

  const total =
    processScore * INSPECTION_WEIGHTS.process +
    visionScore * INSPECTION_WEIGHTS.vision +
    thermalScore * INSPECTION_WEIGHTS.thermal;

  return {
    process: { score: processScore, ok: processOk },
    vision: {
      score: visionScore,
      ok: visionOk,
      defectKey: visionOk ? null : DEFECT_KEYS[Math.floor(rand(8) * DEFECT_KEYS.length)],
      blobs: [
        { cx: 34 + rand(9) * 12, cy: 40 + rand(10) * 12, r: 4 + rand(11) * 3 },
        { cx: 62 + rand(12) * 10, cy: 55 + rand(13) * 14, r: 3 + rand(14) * 3 },
      ],
    },
    thermal: { score: thermalScore, ok: thermalOk, tempMax, tempMin, tempAvg },
    total,
    /* 최종 판정은 거부권 방식: 하나라도 불량이면 불량.
     * total 점수는 참고 지표로만 쓰고 판정에는 관여하지 않는다. */
    failedKeys: [
      !processOk && 'inspectionProcess',
      !visionOk && 'inspectionVision',
      !thermalOk && 'inspectionThermal',
    ].filter(Boolean),
    verdict: (processOk && visionOk && thermalOk) ? 'GOOD' : 'BAD',
  };
};

/* 판정 배지 색 — Shot History 표의 양/불 배지와 같은 팔레트를 쓴다. */
/* 공정검사 실측값 표시용. 실측값은 소수점이 길게 붙어 오므로 2자리에서 끊는다.
   끝자리 0 은 남기지 않는다(34.67, 218.57, 5.2). 숫자가 아니면 그대로 둔다. */
export const round2 = (v) => {
  if (v === null || v === undefined || v === '') return '-';
  const n = Number(v);
  return Number.isFinite(n) ? String(Math.round(n * 100) / 100) : v;
};

export const judgeColor = (ok) => (ok ? '#22C55E' : '#EF4444');
/* 최종 결과 카드의 검사별 막대 색. 종합 점수 막대(판정색)와 구분하기 위한 것이라
   판정 의미는 담지 않는다. 해당 검사가 불량이면 빨강으로 덮어 실패를 우선 보이게 한다. */
export const INSPECTION_BAR_COLORS = {
  inspectionProcess: '#60A5FA',
  inspectionVision: '#A78BFA',
  inspectionThermal: '#F59E0B',
};
export const barColor = (key, ok) => (ok ? INSPECTION_BAR_COLORS[key] : '#EF4444');

export const judgeBg = (ok) => (ok ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)');
